import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Currency, Participant, ReceiptDetail, Trip } from "@/api/types";
import { api } from "@/api/client";
import { useTrip } from "@/hooks/useTrip";
import { useShareTrip } from "@/hooks/useShareTrip";
import {
  useReceipt,
  useConfirmReceipt,
  useToggleConsumer,
  useUpdateLine,
  useDeleteLine,
  useAddLine,
  useDeleteReceipt,
} from "@/hooks/useReceipts";
import { isManualReceipt } from "@/utils/receipt";
import { useParticipants } from "@/hooks/useParticipants";
import { useConflictToast } from "@/hooks/useConflictToast";
import { queryKeys } from "@/hooks/queryKeys";
import { PageShell } from "@/components/PageShell";
import { InTripHeader } from "@/components/InTripHeader";
import { Skeleton } from "@/components/Skeleton";
import { Button } from "@/components/Button";
import { ReceiptPhotoSlot } from "@/components/ReceiptPhotoSlot";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { PaidBySlot } from "@/components/PaidBySlot";
import { ReceiptItemRow } from "@/components/ReceiptItemRow";
import { MoneyAmount } from "@/components/MoneyAmount";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/toast/ToastProvider";
import "./receipt-detail.css";

export function ReceiptDetailScreen() {
  const { slug = "", rid = "" } = useParams<{ slug: string; rid: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  const toast = useToast();

  const tripQ = useTrip(slug);
  const participantsQ = useParticipants(slug);
  const receiptQ = useReceipt(slug, rid);
  const handleShare = useShareTrip(tripQ.data);

  const confirm = useConfirmReceipt(slug, rid);
  const toggleConsumer = useToggleConsumer(slug, rid);
  const updateLine = useUpdateLine(slug, rid);
  const deleteLine = useDeleteLine(slug, rid);
  const addLine = useAddLine(slug, rid);
  const deleteReceipt = useDeleteReceipt(slug);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteReceiptOpen, setDeleteReceiptOpen] = useState(false);

  if (tripQ.isLoading || receiptQ.isLoading || !tripQ.data) {
    // Trip is still loading — no <InTripHeader> can render yet (it needs the
    // trip name + id). The skeleton stands in for the whole screen including
    // the header; this is the only state without a deterministic back link,
    // and it is transient (resolves to a headered state on load).
    return (
      <PageShell>
        <Skeleton variant="block" width="100%" height={56} />
        <Skeleton variant="block" width="100%" height={240} />
      </PageShell>
    );
  }

  const trip = tripQ.data;

  const receipt = receiptQ.data;
  if (!receipt) {
    return (
      <PageShell header={<InTripHeader trip={trip} onShare={handleShare} />}>
        <p>Чек не знайдено.</p>
      </PageShell>
    );
  }

  if (receipt.status === "parsing" || receipt.status === "pending_upload") {
    return (
      <ParsingState
        trip={trip}
        status={receipt.status}
        photoUrl={receipt.photo_url}
        vendorName={receipt.vendor_name}
        onShare={handleShare}
      />
    );
  }
  if (receipt.status === "parse_failed") {
    return (
      <ParseFailedState
        trip={trip}
        rid={rid}
        photoUrl={receipt.photo_url}
        vendorName={receipt.vendor_name}
        onShare={handleShare}
      />
    );
  }

  const participants = participantsQ.data?.participants ?? [];
  // ADR-0031: a manual (photo-less) receipt hides all photo/parse/confirm
  // affordances (AC-20.7). It is born confirmed, so the parsed-only confirm
  // CTA + leave-for-later block below never renders for it anyway.
  const isManual = isManualReceipt(receipt);
  // Read-only is governed by the TRIP lifecycle, not the receipt's confirm
  // state. A confirmed receipt stays editable while the trip is open
  // (AC-08.4 / AC-12.5): the only thing `confirm` removes is the photo
  // (handled separately in <ReceiptPhotoSlot>), so a late consumer can still
  // be tagged on any line. Only closing the trip freezes the receipt.
  const readOnly = trip.status === "closed";

  // "Разом" must reflect the line items actually being split — that's what the
  // balance math sums (domain/services/balances.py), not the AI-extracted
  // source_total snapshot, which goes stale the moment a line is corrected.
  // Sum in integer cents to avoid float drift across many lines.
  const lines = receipt.lines ?? [];
  const lineTotalCents = lines.reduce((acc, l) => acc + Math.round(l.line_total * 100), 0);
  const lineTotal = lineTotalCents / 100;
  const totalCcy = (receipt.source_ccy ?? "EUR") as Currency;
  // Surface the AI's original grand total when it no longer reconciles, so the
  // user can sanity-check that their corrections add up (no missing/duplicate
  // line). It is a reference only — never used for balances.
  const aiTotalMismatch =
    receipt.source_total != null &&
    Math.round(receipt.source_total * 100) !== lineTotalCents;

  return (
    <PageShell
      header={
        <InTripHeader
          trip={trip}
          status={receipt.status}
          source={receipt.source}
          updatedAt={receipt.updated_at}
          onShare={handleShare}
        />
      }
    >
      <article className="wp-detail">
        {isManual ? null : (
          <ReceiptPhotoSlot
            status={receipt.status}
            photoUrl={receipt.photo_url}
            photoDeletedAt={receipt.photo_deleted_at}
            vendorName={receipt.vendor_name}
          />
        )}
        <ReceiptCurrencyAndPayer
          receipt={receipt}
          participants={participants}
          slug={slug}
          rid={rid}
          readOnly={readOnly}
          isManual={isManual}
          onConflict={(e) => onConflict(e, [queryKeys.receipt(slug, rid)])}
          onUpdated={() => qc.invalidateQueries({ queryKey: queryKeys.receipt(slug, rid) })}
        />

        <h2 className="wp-detail__overline">Позиції</h2>
        <ol className="wp-detail__lines">
          {(receipt.lines ?? []).map((line) => (
            <ReceiptItemRow
              key={line.line_id}
              line={line}
              ccy={(receipt.source_ccy ?? "EUR") as Currency}
              participants={participants}
              optedOutPids={new Set(receipt.opted_out_participant_ids ?? [])}
              readOnly={readOnly}
              onToggleConsumer={(lineId, pid, currentlyTagged) =>
                toggleConsumer.mutate({
                  lineId,
                  participantId: pid,
                  currentlyTagged,
                  lineVersion: line.version,
                })
              }
              onToggleSharedByAll={(lineId, next) => {
                if (next) {
                  void api
                    .applySharedByAll(slug, rid, lineId, { version: line.version })
                    .then(() =>
                      qc.invalidateQueries({ queryKey: queryKeys.receipt(slug, rid) }),
                    )
                    .catch((err) => onConflict(err, [queryKeys.receipt(slug, rid)]));
                } else {
                  void api
                    .removeSharedByAll(slug, rid, lineId)
                    .then(() =>
                      qc.invalidateQueries({ queryKey: queryKeys.receipt(slug, rid) }),
                    );
                }
              }}
              onEditLine={(lineId, patch) => updateLine.mutate({ lineId, patch })}
              onRequestDeleteLine={(lineId) =>
                setPendingDelete({ id: lineId, name: line.name })
              }
            />
          ))}
        </ol>
        {!readOnly ? (
          <AddLineForm
            onAdd={(body) =>
              addLine.mutate(body, {
                onError: () =>
                  toast.push({ tone: "error", title: "Не вдалось додати позицію" }),
              })
            }
            pending={addLine.isPending}
          />
        ) : null}

        {lines.length > 0 ? (
          <div className="wp-detail__totals">
            <p className="wp-detail__total">
              Разом{" "}
              <MoneyAmount value={lineTotal} ccy={totalCcy} size="h1" />
            </p>
            {aiTotalMismatch ? (
              <p className="wp-detail__total-note">
                AI зчитав{" "}
                <MoneyAmount
                  value={receipt.source_total!}
                  ccy={totalCcy}
                  size="caption"
                />
                . «Разом» рахується за позиціями.
              </p>
            ) : null}
          </div>
        ) : null}

        {receipt.status === "parsed" ? (
          <>
            <Button
              size="lg"
              fullWidth
              onClick={() => setConfirmOpen(true)}
              disabled={!receipt.paid_by_participant_id}
              data-testid="confirm-receipt"
            >
              Підтвердити розпарсинг
            </Button>
            <p className="wp-detail__hint">
              Після підтвердження фото зникне. Назад нема.
            </p>

            {/* Leave-for-later (AC-07.8). A half-done receipt is a valid
                persisted `parsed` row — nothing is lost by leaving. So the
                exit reads calm, not like an abandon: a quiet success line +
                a ghost in-trip back link. No confirm modal here; the only
                modal in this flow is confirm-parse (photo deletion). */}
            <div className="wp-detail__leave" data-testid="leave-for-later">
              <p className="wp-detail__leave-note">
                <span aria-hidden="true" className="wp-detail__leave-check">
                  ✓
                </span>{" "}
                Збережено. Можна дозаповнити пізніше.
              </p>
              <Link
                to={`/t/${slug}/receipts`}
                className="wp-btn wp-btn--ghost wp-btn--md wp-btn--full"
                data-testid="leave-to-receipts"
              >
                <span className="wp-btn__label">‹ До чеків</span>
              </Link>
            </div>
          </>
        ) : null}

        {/* Manual-receipt delete escape hatch (AC-20.16 / AC-03.8) — Open only.
            A manual receipt has no attention-filter row to delete from, so the
            destructive action lives here on the detail screen. */}
        {isManual && !readOnly ? (
          <Button
            variant="danger"
            fullWidth
            onClick={() => setDeleteReceiptOpen(true)}
            data-testid="delete-receipt"
          >
            Видалити чек
          </Button>
        ) : null}
      </article>

      <Modal
        open={deleteReceiptOpen}
        title="Видалити цей чек?"
        description="Витрату буде видалено назавжди."
        variant="destructive"
        confirmLabel="Видалити"
        onCancel={() => setDeleteReceiptOpen(false)}
        onConfirm={() => {
          setDeleteReceiptOpen(false);
          deleteReceipt.mutate(rid, {
            onSuccess: () => navigate(`/t/${slug}/receipts`),
          });
        }}
      />

      <Modal
        open={confirmOpen}
        title="Підтвердити розпарсинг?"
        description="Фото буде видалене назавжди. Скасувати не можна."
        confirmLabel="Підтвердити"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          confirm.mutate(
            { version: receipt.version },
            {
              onSuccess: () =>
                toast.push({ tone: "success", title: "Чек підтверджено" }),
            },
          );
        }}
      />

      <Modal
        open={pendingDelete !== null}
        title={
          pendingDelete
            ? `Видалити позицію «${pendingDelete.name}»?`
            : "Видалити позицію?"
        }
        variant="destructive"
        confirmLabel="Видалити"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const lid = pendingDelete?.id;
          setPendingDelete(null);
          if (lid) deleteLine.mutate({ lineId: lid });
        }}
      />
    </PageShell>
  );
}

/**
 * Always-visible dashed strip per receipt-detail.md wireframe. Idle state
 * shows the "+ Додати позицію" prompt; tapping swaps the same container's
 * content to a 2-field form (name + unit_price). Quantity defaults to 1
 * on submit — users tweak qty inline on the new row in the rare cases
 * they need a different value.
 */
function AddLineForm({
  onAdd,
  pending,
}: {
  onAdd: (body: { name: string; quantity: number; unit_price: number }) => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");

  function reset() {
    setName("");
    setPrice("");
  }

  if (!open) {
    return (
      <button
        type="button"
        className="wp-detail__add-line wp-detail__add-line--idle"
        onClick={() => setOpen(true)}
        data-testid="add-line-open"
      >
        + Додати позицію
      </button>
    );
  }

  const canSubmit =
    name.trim() !== "" &&
    Number.isFinite(Number(price)) &&
    Number(price) >= 0;

  return (
    <form
      className="wp-detail__add-line wp-detail__add-line--form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onAdd({
          name: name.trim(),
          quantity: 1,
          unit_price: Number(price),
        });
        reset();
        setOpen(false);
      }}
    >
      <input
        type="text"
        className="wp-detail__add-line-name"
        placeholder="Назва"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
        data-testid="add-line-name"
        aria-label="Назва нової позиції"
      />
      <input
        type="number"
        inputMode="decimal"
        step="any"
        min={0}
        className="wp-detail__add-line-price"
        placeholder="Ціна"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        data-testid="add-line-price"
        aria-label="Ціна"
      />
      <div className="wp-detail__add-line-actions">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Скасувати
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!canSubmit}
          loading={pending}
          data-testid="add-line-submit"
        >
          Додати
        </Button>
      </div>
    </form>
  );
}

function ReceiptCurrencyAndPayer({
  receipt,
  participants,
  slug,
  rid,
  readOnly,
  isManual,
  onConflict,
  onUpdated,
}: {
  receipt: ReceiptDetail;
  participants: ReadonlyArray<Participant>;
  slug: string;
  rid: string;
  readOnly: boolean;
  isManual: boolean;
  onConflict: (err: unknown) => boolean;
  onUpdated: () => void;
}) {
  const ccyValue: Currency = receipt.source_ccy ?? "EUR";

  function updateCcy(next: Currency) {
    void api
      .updateReceipt(slug, rid, { source_ccy: next, version: receipt.version })
      .then(onUpdated)
      .catch(onConflict);
  }
  function setPaidBy(pid: string) {
    void api
      .updateReceipt(slug, rid, {
        paid_by_participant_id: pid,
        version: receipt.version,
      })
      .then(onUpdated)
      .catch(onConflict);
  }

  const paid =
    participants.find((p) => p.participant_id === receipt.paid_by_participant_id) ??
    null;

  return (
    <div className="wp-detail__top">
      <CurrencyPicker
        label="Валюта чеку"
        value={ccyValue}
        onChange={updateCcy}
        disabled={readOnly}
        // No «AI визначив» hint for a manual receipt — there was no AI (AC-20.7).
        hint={isManual ? undefined : "AI визначив. Можна виправити."}
      />
      <PaidBySlot
        participant={paid}
        participants={participants}
        disabled={readOnly}
        onChange={setPaidBy}
      />
    </div>
  );
}

function ParsingState({
  trip,
  status,
  photoUrl,
  vendorName,
  onShare,
}: {
  trip: Trip;
  status: "parsing" | "pending_upload";
  photoUrl: string | null | undefined;
  vendorName: string | null | undefined;
  onShare: () => void;
}) {
  return (
    // status="parsing" hides the meta line in <InTripHeader>; the header
    // now owns the in-trip exit, so the body «← До списку» anchor is gone.
    <PageShell header={<InTripHeader trip={trip} status={status} onShare={onShare} />}>
      <div className="wp-detail__parsing">
        <h2 className="wp-detail__overline">
          {status === "parsing" ? "AI читає чек…" : "Завантажую…"}
        </h2>
        <ReceiptPhotoSlot
          status={status}
          photoUrl={photoUrl}
          photoDeletedAt={null}
          vendorName={vendorName}
        />
        <p className="wp-detail__hint">
          Це зазвичай триває 3–15 секунд. Можеш повернутись назад — продовжимо у фоні.
        </p>
        <Skeleton variant="block" width="100%" height={48} />
        <Skeleton variant="block" width="100%" height={48} />
      </div>
    </PageShell>
  );
}

function ParseFailedState({
  trip,
  rid,
  photoUrl,
  vendorName,
  onShare,
}: {
  trip: Trip;
  rid: string;
  photoUrl: string | null | undefined;
  vendorName: string | null | undefined;
  onShare: () => void;
}) {
  const slug = trip.trip_id;
  const qc = useQueryClient();
  const toast = useToast();
  const [retrying, setRetrying] = useState(false);
  return (
    <PageShell header={<InTripHeader trip={trip} status="parse_failed" onShare={onShare} />}>
      <div className="wp-detail__failed">
        <ReceiptPhotoSlot
          status="parse_failed"
          photoUrl={photoUrl}
          photoDeletedAt={null}
          vendorName={vendorName}
        />
        <h1 className="wp-detail__title">Не вдалось розпізнати</h1>
        <p>AI не зрозумів зображення. Можеш спробувати ще раз або ввести позиції вручну.</p>
        <Button
          loading={retrying}
          onClick={async () => {
            setRetrying(true);
            try {
              await api.parseReceipt(slug, rid);
              await qc.invalidateQueries({ queryKey: queryKeys.receipt(slug, rid) });
            } catch {
              toast.push({ tone: "error", title: "Не вдалося — спробуй пізніше." });
            } finally {
              setRetrying(false);
            }
          }}
        >
          Спробувати ще
        </Button>
      </div>
    </PageShell>
  );
}

